const RESET_BALANCE_TASKS_KEY = "resetBalanceTasks"

// Absolute backstop for a task that exists but never concludes (stuck Celery
// task, bad DB row) - 404 detection alone can't catch this since the backend
// keeps responding. See PRD "Amendment: localStorage persistence + staleness
// guard".
export const RESET_BALANCE_TASK_TTL_MS = 10 * 60 * 1000

interface IPersistedResetBalanceTask {
  classId: string
  taskId: string
  timestamp: number
}

const readPersistedResetBalanceTasks = (): IPersistedResetBalanceTask[] => {
  const raw = localStorage.getItem(RESET_BALANCE_TASKS_KEY)
  if (!raw) return []

  try {
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

const writePersistedResetBalanceTasks = (
  tasks: IPersistedResetBalanceTask[]
) => {
  localStorage.setItem(RESET_BALANCE_TASKS_KEY, JSON.stringify(tasks))
}

export const getPersistedResetBalanceTaskId = (
  classId: string
): string | null => {
  const task = readPersistedResetBalanceTasks().find(
    (task) => task.classId === classId
  )
  return task?.taskId ?? null
}

export const setPersistedResetBalanceTaskId = (
  classId: string,
  taskId: string
) => {
  const tasks = readPersistedResetBalanceTasks().filter(
    (task) => task.classId !== classId
  )
  tasks.push({ classId, taskId, timestamp: Date.now() })
  writePersistedResetBalanceTasks(tasks)
}

export const clearPersistedResetBalanceTaskId = (classId: string) => {
  const tasks = readPersistedResetBalanceTasks().filter(
    (task) => task.classId !== classId
  )
  writePersistedResetBalanceTasks(tasks)
}

export const isPersistedResetBalanceTaskStale = (classId: string): boolean => {
  const task = readPersistedResetBalanceTasks().find(
    (task) => task.classId === classId
  )
  if (!task) return false

  return Date.now() - task.timestamp > RESET_BALANCE_TASK_TTL_MS
}
